import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { ILink } from 'src/app/api/modules/core/components/abstract/ILink';
import { AppService } from '../../../services/application/application.service';

/**
 * An AbstractLink can directly wrap a local component with an `<a>` and capture that click to send it
 * through a common linking service. The default link behavior is prevented and replaced with our own.
 *
 * See [Link]{@link ILink}
 *
 * ### CSS Custom Properties
 *
 * Set these from any ancestor to restyle the component — they inherit through
 * view encapsulation, so no `::ng-deep` or `:host-context` is needed.
 *
 * | Property | Description | Default |
 * | --- | --- | --- |
 * | `--ds-abstract-link-position` | Host position | `static` |
 * | `--ds-abstract-link-height` | Host height | `auto` |
 * | `--ds-abstract-link-anchor-display` | Anchor display | `inline` |
 * | `--ds-abstract-link-anchor-width` | Anchor width | `auto` |
 * | `--ds-abstract-link-anchor-height` | Anchor height | `auto` |
 * | `--ds-abstract-link-color` | Anchor text/icon color (never user-agent link blue) | `inherit` |
 * | `--ds-abstract-link-focus-width` | Focus outline thickness | `2px` |
 * | `--ds-abstract-link-focus-radius` | Focus outline radius | `0` |
 */
@Component({
  selector: 'app-abstract-link',
  templateUrl: './abstract-link.component.html',
  styleUrls: ['./abstract-link.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AbstractLinkComponent {
  private readonly appService = inject(AppService);

  /**
   * Input configuration for the link
   */
  readonly config = input<ILink | null>();

  /**
   * Optional ARIA label for accessibility.
   * When provided, it will be applied to the <a> element to provide an accessible name.
   */
  readonly ariaLabel = input<string | null>();

  /**
   * Output occurs when no actionable href is provided
   */
  readonly clicked = output<MouseEvent | PointerEvent | TouchEvent>();

  /**
   * Occurs when the link has been touched
   * @param e - the native click event
   */
  protected onClicked(e: MouseEvent | PointerEvent | TouchEvent) {
    this.appService.registerFirstInteraction();

    // Always prevent default navigation since we control all link behavior
    e.preventDefault();
    e.stopImmediatePropagation();

    const config = this.config();
    if (config?.href && config.type !== 'none') {
      this.appService.openLink(config);
    } else {
      this.clicked.emit(e);
    }
  }
}
